/* eslint-disable no-nested-ternary */
import React, { ChangeEvent, useRef, useState } from 'react';
import { ButtonBase, InputBase, makeStyles } from '@material-ui/core';
import { optimoveEvents } from 'src/screens/optimove';
import COLORS from 'src/lib/colors';
import { useProductBrowseContext } from 'src/lib/contexts/product-browse-context';
import useEventsReporter from 'src/hooks/useEventsReporter';
import eventMetricsMap from 'src/hooks/useEventsReporter/event-types';
import { storeToLocalStorage } from 'src/lib/utils/store-data-to-local-storage';
import ROUTES from 'src/lib/routes';
import { mergeClasses } from 'src/stylesheets/utils';
import { useRouter, useUserInfo } from 'src/hooks';
import { useTabsUIStateContext } from 'src/lib/contexts/tabs-ui-state-context';
import Brcd from './BarcodeScanner/Brcd';
import BarcodeScanner from './BarcodeScanner';

const useStyles = makeStyles((theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        position: 'relative',
        '& #product-search-input::-webkit-search-cancel-button': {
            display: 'none !important',
        },
        '& input::-webkit-input-placeholder': {
            color: '#757575 !important',
            opacity: 1,
        },
    },
    searchInput: {
        display: 'flex',
        width: '100%',
        flexGrow: 1,
        height: '38px',
        padding: '0.25rem 0',
        borderRadius: '0.625rem',
        backgroundColor: COLORS.primaryWhite,
        gap: '10px',
        color: '#000000',
    },
    searchInputLarge: {
        height: '50px',
        [theme.breakpoints.down(395)]: {
            height: '46px',
        },
        [theme.breakpoints.down(376)]: {
            height: '43px',
        },
    },

    searchIconBtn: {
        marginLeft: '.9rem',
    },
    searchIcon: {
        // width: '1.125rem',
    },
    clearIconBtn: {
        width: '1.75rem',
        height: '1.75rem',
        marginRight: '.5rem',
    },
    clearIcon: {
        width: '1.75rem',
        height: '1.75rem',
        color: COLORS.fordGray,
    },
    barcodeScan: {
        position: 'absolute',
        right: '10px',
        top: '10px',
    },
    barcodeScanAlign: {
        top: '15px',
    },
}));

/** Search box for searching for products or starting the browse flow */
export default function SearchForProduct({
    /** if true, clicking the component will immediately begin the product browsing */
    decoy,
    // possibly have a separate handleSubmit here in the useProductBrowseContext
    handleSubmit,
    mainStore,
    autoFocus = false,
    hideBarcodeScannerIcon,
    inStoreMode,
    ...rest
}: any) {
    const [updatingSearch, setUpdatingSearch] = useState(false);
    const { begin, searchString, setSearchString, formik } =
        useProductBrowseContext();
    const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
    const { customEvent, analyticsCustomEvent } = useEventsReporter();
    const classes = useStyles();
    const ref = useRef();
    const { location } = useRouter();
    const { fromPath } = useProductBrowseContext();
    const { userInfo } = useUserInfo();
    const { setProductLinkInNative } = useTabsUIStateContext();

    function onSubmit(event: any) {
        if (event) {
            event.preventDefault();
        }
        setUpdatingSearch(false);
        setProductLinkInNative({ src: '', show: false, route: '' });
        // condition added to prevent searching when search icon is clicked or enter is pressed with empty input
        if (searchString?.trim() !== '') {
            customEvent(eventMetricsMap.searched_product);
            let eventName = eventMetricsMap.product_search;
            if (userInfo && fromPath?.includes('store-mode')) {
                eventName = eventMetricsMap['storemode_logged in'];
            } else if (!userInfo && fromPath?.includes('store-mode')) {
                eventName = eventMetricsMap.storemode_guest;
            } else {
                eventName = eventMetricsMap.product_search;
            }

            analyticsCustomEvent(eventName, {
                click_text: 'search performed',
                element_location: 'search bar',
                search_term: searchString,
            });

            optimoveEvents(eventMetricsMap.product_search, {
                click_text: 'search performed',
                element_location: 'search bar',
                search_term: searchString,
            });
            localStorage.setItem('searchedQuery', searchString);
            handleSubmit(searchString.trim());
            storeToLocalStorage('searchHistory', searchString);
        }
    }

    function handleDecoy(event: any, eventCall = false) {
        if (eventCall) {
            // const eventName = fromPath?.includes('stores') ? eventMetricsMap.stores : eventMetricsMap.home;
            let eventName = null;
            if (userInfo && fromPath?.includes('store-mode')) {
                eventName = eventMetricsMap['storemode_logged in'];
            } else if (!userInfo && fromPath?.includes('store-mode')) {
                eventName = eventMetricsMap.storemode_guest;
            } else if (location.pathname.includes('stores')) {
                eventName = eventMetricsMap.stores;
            } else if (location.pathname.includes('home')) {
                eventName = eventMetricsMap.home;
            }
            if (eventName) {
                analyticsCustomEvent(eventName, {
                    click_text: 'search clicked',
                    element_location: 'search bar',
                });
            }
        }

        if (decoy) {
            if (event) {
                event.preventDefault();
            }
            if (ref?.current) {
                (ref as any).current.blur();
            }
            begin();
        }
    }
    function onUpdateSearch(
        event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) {
        setUpdatingSearch(true);
        setSearchString(event.target.value);
    }
    function clearSearch() {
        setSearchString('');
    }

    if (!mainStore) {
        return null;
    }

    return (
        <form
            className={classes.form}
            onSubmit={onSubmit}
            id="product-search-input-container"
        >
            <InputBase
                tabIndex={decoy ? -1 : undefined}
                id="product-search-input"
                className={
                    inStoreMode
                        ? mergeClasses(
                              classes.searchInput,
                              classes.searchInputLarge
                          )
                        : classes.searchInput
                }
                placeholder="Search"
                onChange={onUpdateSearch}
                onClick={handleDecoy}
                onFocus={(e) => handleDecoy(e, true)}
                autoFocus={autoFocus}
                onSubmit={onSubmit}
                inputRef={ref}
                inputMode="search"
                type="search"
                aria-label="Search"
                aria-selected="true"
                value={
                    decoy
                        ? location.pathname === ROUTES.productDetailsPage
                            ? searchString
                            : undefined
                        : searchString
                }
                inputProps={{
                    tabIndex: decoy ? -1 : undefined,
                    'data-testid': 'searchInputBox',
                }}
                error={
                    formik?.touched?.searchString &&
                    Boolean(formik?.errors?.searchString)
                }
                helpertext={
                    formik?.touched?.searchString &&
                    formik?.errors?.searchString
                }
                startAdornment={
                    <ButtonBase
                        tabIndex={decoy ? -1 : undefined}
                        onClick={decoy ? handleDecoy : onSubmit}
                        id="product-search-button"
                        className={classes.searchIconBtn}
                    >
                        <img
                            id="product-search-icon-img"
                            src={require('../assets/icons/Search_icon_home.svg')}
                            className={classes.searchIcon}
                            alt="search icon"
                        />
                    </ButtonBase>
                }
                endAdornment={
                    !decoy && !!searchString ? (
                        <ButtonBase
                            tabIndex={decoy ? -1 : undefined}
                            onClick={decoy ? handleDecoy : clearSearch}
                            id="product-search-clear-button"
                            className={classes.clearIconBtn}
                        >
                            <img
                                id="product-search-clear-img"
                                src={require('../assets/icons/close-icon.svg')}
                                alt="Clear search icon"
                            />
                        </ButtonBase>
                    ) : undefined
                }
                {...rest}
            />
            {!updatingSearch && !hideBarcodeScannerIcon ? (
                <ButtonBase
                    className={
                        inStoreMode
                            ? mergeClasses(
                                  classes.barcodeScan,
                                  classes.barcodeScanAlign
                              )
                            : classes.barcodeScan
                    }
                    onClick={() => {
                        setIsBarcodeScannerOpen(true);
                        if (
                            userInfo?.rewardsNumber &&
                            location.pathname.includes('store-mode')
                        ) {
                            analyticsCustomEvent(
                                eventMetricsMap['storemode_logged in'],
                                {
                                    click_text: 'scan barcode',
                                    element_location: 'search bar',
                                }
                            );
                        } else {
                            analyticsCustomEvent(eventMetricsMap.scan, {
                                click_text: 'scan icon',
                            });
                        }
                    }}
                    role="button"
                    aria-label="Scan"
                >
                    <img
                        src={require('../assets/icons/barcode-scanner.svg')}
                        alt="Scan"
                        style={{ height: '18px', marginLeft: '1.5rem' }}
                    />
                </ButtonBase>
            ) : undefined}
            {isBarcodeScannerOpen && (
                <BarcodeScanner
                    isScannerOpen={isBarcodeScannerOpen}
                    onClose={() => setIsBarcodeScannerOpen(false)}
                />
            )}
        </form>
    );
}
